<template>
  <div
    :id="'check-link-' + id"
    v-click-outside="handleClickOutside"
    class="tw-group"
    :class="[
      {
        'sf-hover': !followedByCurrentUser,
        'sf-commcard-user-can-check': currentUserCanCheck,
      },
      checkStateClass,
      highlightClass,
    ]"
  >
    <div class="sf-commcard-message sf-form-check-content">
      <div class="sf-commcard-message-content">
        <div class="sf-commcard-message-content-toggle tw-min-w-[17px]">
          <div
            v-if="!isCheckNotApplicable"
            v-tooltip.html="tooltipHtmlContent"
            class="sf-form-check-indicator sf-pointer"
            @click.prevent="currentUserCanCheck ? handleCheckClick() : null"
          ></div>
        </div>
        <div class="sf-commcard-message-content-post sf-commcard-post">
          <div
            v-if="showPeriod"
            class="sf-commcard-post-period sf-label sf-label-rectangular"
          >
            {{ ledgerPeriodName }}
          </div>

          <div
            v-if="isCheckNotApplicable"
            v-dompurify-html="markedNotApplicableText"
            class="tw-uppercase tw-text-neutral-600"
          ></div>

          <div
            v-dompurify-html="renderedText"
            class="sf-commcard-post-text"
            :class="{ 'tw-line-through': isCheckNotApplicable }"
          ></div>

          <div v-if="showContextBtn && !isCompanyContext" class="sf-pad-b-2">
            <SfButtonDs :href="contextUrl" size="sm">{{
              contextBtn
            }}</SfButtonDs>
          </div>

          <div class="sf-commcard-post-actions">
            <a
              v-if="showCommentsToggle"
              href="#"
              @click.prevent="toggleShowAllComments"
              >{{
                I18n.t(
                  'communication.comments.show_all_toggle.' +
                    commentsVisibilityState,
                  { count: allCommentsCount }
                )
              }}</a
            >

            <a
              v-if="
                !showNewCommentForm &&
                !showCommentsToggle &&
                !disableInteraction
              "
              href="#"
              @click.prevent="addNewCommentCheckId(id)"
              >{{ I18n.t('communication.comments.add') }}</a
            >

            <a
              v-if="allowedActions.toggleApplicable && !checkinsClosed"
              class="group-hover:tw-block tw-hidden !tw-text-neutral-700"
              href="#"
              @click.prevent="toggleApplicable"
            >
              {{
                isCheckNotApplicable
                  ? I18n.t('components.check_link.mark_as_applicable')
                  : I18n.t('components.check_link.mark_as_not_applicable')
              }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div
      class="sf-commcard-comments"
      :class="theme.newLayout && theme.commentsBandBackground"
    >
      <CommentCard
        v-for="comment in comments"
        :key="comment.id"
        :commentable-id="id"
        commentable-type="CheckLink"
        :ledger-id="ledgerId"
        :company-id="companyId"
        v-bind="comment"
      ></CommentCard>
    </div>
    <div
      v-if="
        commentsVisibilityState === 'all' &&
        !showNewCommentForm &&
        !disableInteraction
      "
      class="sf-commcard-reply-placeholder"
      @click="addNewCommentCheckId(id)"
    >
      {{ I18n.t('communication.comments.enter_reply') }}
    </div>
    <CommentForm
      v-if="showNewCommentForm"
      :commentable-id="id"
      :follower-ids="followerIds"
      commentable-type="CheckLink"
      :ledger-id="ledgerId"
      :company-id="companyId"
      :variant="theme.newLayout ? 'seamless' : 'default'"
      :placeholder="
        theme.newLayout ? I18n.t('communication.comments.enter_reply') : ''
      "
      text=""
    ></CommentForm>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia'
import { mapActions, mapGetters, mapState } from 'vuex'

import commentsVisibility from '../mixins/comments-visibility'
import CommentCard from '../comments/comment-card'
import CommentForm from '../comments/comment-form'
import { isCurrentWorkflowLocked } from '@app/adapters/silverfin'
import useCheckHelpers from '@app/application/communication/sections/communication/composables/use-check-helpers'
import { useHighlightedCard } from '@app/shared/pinia/highlighted-card'
import { SfButtonDs } from '@silverfin/ui-components'
import { anyCheckinFromOtherUsers } from '@app/application/communication/utils'
import { commsTheme } from '@app/shared/comms-theme'

export default {
  components: {
    SfButtonDs,
    CommentCard,
    CommentForm,
  },
  mixins: [commentsVisibility],
  props: {
    id: {
      type: Number,
      required: true,
    },
    markedNotApplicableBy: {
      type: String,
      default: null,
    },
    markedNotApplicableAt: {
      type: String,
      default: null,
    },
    ledgerCheckListId: {
      type: Number,
      required: true,
    },
    contextId: {
      type: Number,
      required: true,
    },
    contextType: {
      type: String,
      required: true,
    },
    renderedText: {
      type: String,
      required: true,
    },
    followerIds: {
      type: Array,
      required: true,
    },
    checkinIds: {
      type: Array,
      required: true,
    },
    checkinUserIds: {
      type: Array,
      required: true,
    },
    commentIds: {
      type: Array,
      required: true,
    },
    allowAnyUser: {
      type: Boolean,
      default: false,
    },
    allowedActions: {
      type: Object,
      required: true,
    },
    checkListId: {
      type: Number,
      required: true,
    },
    defaultCommentsVisibilityState: {
      type: String,
      default: 'default',
    },
    showPeriod: {
      type: Boolean,
      default: false,
    },
    ledgerPeriodName: {
      type: String,
      required: true,
    },
    ledgerId: {
      type: Number,
      required: true,
    },
    showContextBtn: {
      type: Boolean,
      default: false,
    },
    openReply: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['toggle-check-applicable'],
  setup() {
    return {
      ...useCheckHelpers(),
      disableInteraction: isCurrentWorkflowLocked(),
      theme: commsTheme(),
    }
  },
  computed: {
    ...mapState({
      checkLists: (state) => state.checks.checkLists,
      newCommentCheckIds: (state) => state.ui.newCommentCheckIds,
      storeLedgerId: (state) => state.ledgerId,
    }),

    ...mapPiniaState(useHighlightedCard, ['highlightedCard']),

    ...mapGetters([
      'contextByLedgerTypeAndId',
      'commentsByIds',
      'checkCheckins',
      'userById',
      'checkinDateByCheckAndUser',
    ]),

    isCheckNotApplicable() {
      return !!this.markedNotApplicableBy
    },

    anyUserId() {
      const anyUserCheckin = this.checkCheckins(this.id).filter(
        (checkin) => checkin.anyUserId
      )
      if (_.isEmpty(anyUserCheckin)) return null

      return _.first(anyUserCheckin).anyUserId
    },

    anyUserName() {
      const user = this.userById(this.anyUserId)
      if (!user) return ''

      return user.name
    },

    contextBtn() {
      return I18n.t(
        'communication.cards.caption_btn.' + _.snakeCase(this.contextType)
      )
    },

    contextUrl() {
      return (
        this.contextByLedgerTypeAndId(
          this.storeLedgerId || this.ledgerId,
          this.contextType,
          this.contextId
        ).url +
        '?current_ledger_id=' +
        this.ledgerId
      )
    },

    companyId() {
      return parseInt(window.Silverfin.currentCompanyId, 10)
    },

    isCompanyContext() {
      return this.contextType === 'Company'
    },

    participantIds() {
      return (
        this.checkLists?.[this.ledgerId]?.[this.checkListId]?.participantIds ||
        []
      ).sort()
    },

    checkedByIds() {
      return this.checkCheckins(this.id)
        .filter((checkin) => !checkin.anyUserId)
        .flatMap((checkin) => [checkin.userId, checkin.assignedUserId])
    },

    checkedByAnyUserIds() {
      return this.checkCheckins(this.id)
        .map((checkin) => checkin.anyUserId)
        .filter((anyUserId) => anyUserId)
    },

    parsedCurrentUserId() {
      return parseInt(Silverfin.currentUserId())
    },

    currentUserCanCheck() {
      return (
        !this.disableInteraction &&
        (this.participantIds.includes(this.parsedCurrentUserId) ||
          this.allowAnyUser)
      )
    },

    checkedByCurrentUser() {
      if (!this.allowAnyUser) {
        return this.checkedByIds.includes(this.parsedCurrentUserId)
      } else {
        const isCurrentUserCheck = this.participantIds.includes(
          this.parsedCurrentUserId
        )
        const isCheckedByCurrentUser = this.checkedByIds.includes(
          this.parsedCurrentUserId
        )

        // when check can be checked by current user and "anybody" user, then checking own check has higher priority
        if (isCurrentUserCheck) {
          return isCheckedByCurrentUser
          // when check has the only option - "anybody", then we check if any other user checked it before
        } else {
          return this.checkedByAnyUserIds.length > 0
        }
      }
    },

    checkinsEmpty() {
      return this.checkedByIds.length + this.checkedByAnyUserIds.length === 0
    },

    checkinsClosed() {
      return this.checkCheckedByAllParticipants({
        checkinIds: this.checkinIds,
        checkListId: this.checkListId,
      })
    },

    checkinsPartial() {
      return (
        !this.checkinsEmpty &&
        !this.checkinsClosed &&
        (this.currentUserCanCheck ? this.checkedByCurrentUser : true)
      )
    },

    checkStateClass() {
      if (this.checkinsEmpty) {
        return ''
      } else if (this.checkinsPartial) {
        return 'sf-commcard-partially'
      } else if (this.checkinsClosed) {
        return 'sf-commcard-completed'
      } else {
        return ''
      }
    },

    followedByCurrentUser() {
      return this.followerIds.includes(this.parsedCurrentUserId)
    },

    comments() {
      switch (this.commentsVisibilityState) {
        case 'default':
          return []
        case 'all':
          return this.allComments
        case 'none':
          return []
        default:
          return []
      }
    },

    showNewCommentForm() {
      return this.openReply || this.newCommentCheckIds.includes(this.id)
    },

    tooltipParticipantsList() {
      return this.participantIds
        .map((participantId) => {
          // see if there's a checkin for this participant
          const checkin = this.checkCheckins(this.id).find(
            (checkin) => checkin.assignedUserId === participantId
          )
          // if there's a checkin, use the "userId" which is the original user that created the checkin (which might be different than the assigned participant)
          // otherwise, use the participantId
          const userId = checkin
            ? checkin.userId || checkin.assignedUserId
            : participantId

          const checkinDate = this.checkinDateByCheckAndUser[this.id]?.[userId]

          const checkinDateText = checkinDate ? `(${checkinDate})` : ''

          return `
            <div class="sf-level sf-below-1">
              <div>
                <div class="sf-form-check-indicator ${
                  this.checkedByIds.includes(userId) ? 'sf-bg-primary' : ''
                }">
                </div>
              </div>
              <div>
                ${this.userById(userId).name}
                ${checkinDateText}
              </div>
            </div>
          `
        })
        .join('')
    },
    tooltipAnyUserMention() {
      if (!this.allowAnyUser) return ''

      const anyUserCheckinDate =
        this.checkinDateByCheckAndUser[this.id]?.[this.anyUserId]

      const anyUserInfo = [this.anyUserName, anyUserCheckinDate].filter(Boolean)

      const checkedByAnyUser =
        anyUserInfo.length > 0 ? `(${anyUserInfo.join(', ')})` : ''

      return `<div class="sf-level sf-below-1">
           <div>
             <div class="sf-form-check-indicator ${
               this.checkedByAnyUserIds.length > 0 ? 'sf-bg-primary' : ''
             }">
             </div>
           </div>
           <div>
            ${I18n.t(
              'components.ledger_check_list.single.any_user'
            )} ${checkedByAnyUser}
           </div>
         </div>`
    },
    tooltipHtmlContent() {
      return this.tooltipParticipantsList + this.tooltipAnyUserMention
    },

    highlighted() {
      const { type, id } = this.highlightedCard
      const isHighlighted = type === 'check-link' && id === this.id
      if (isHighlighted) {
        this.toggleShowAllComments()
      }
      return isHighlighted
    },

    highlightClass() {
      if (!this.highlightedCard.id) return ''
      return this.highlighted ? '' : 'sf-dim-el'
    },

    markedNotApplicableText() {
      return (
        I18n.t('components.check_link.marked_as_not_applicable_by', {
          name: this.markedNotApplicableBy,
        }) +
        (this.markedNotApplicableAt ? ` (${this.markedNotApplicableAt})` : '')
      )
    },
  },

  created() {
    this.SHOW_COMMENTS_TOGGLE_TRESHOLD = 0
    this.setCommentsVisibilityState(this.defaultCommentsVisibilityState)
  },

  methods: {
    ...mapActions(['deleteCheckin', 'addCheckin', 'addNewCommentCheckId']),

    ...mapPiniaActions(useHighlightedCard, ['unsetHighlightedCard']),

    handleClickOutside() {
      if (this.highlighted) {
        this.unsetHighlightedCard()
      }
    },

    handleCheckClick() {
      if (this.checkedByCurrentUser) {
        this.deleteCheckin(this.id)
      } else {
        this.addCheckin(this.id)
      }
    },

    toggleApplicable() {
      this.$emit('toggle-check-applicable', {
        checkLinkId: this.id,
        ledgerCheckListId: this.ledgerCheckListId,
        withConfirm: anyCheckinFromOtherUsers(this.checkinUserIds),
      })
    },
  },
}
</script>
